import React, { useRef } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import {
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  styled,
  useTheme,
} from "@mui/material";
import { getUnitForQuantity } from "../utils";

const SelectContainer = styled("div")({
  display: "flex",
  width: "100%",
  position: "relative",
});

const StyledSelect = styled(Select)({
  width: "100%",
});

const Label = styled("label")({
  fontSize: "16px",
  fontWeight: 500,
  lineHeight: "21px",
  marginBottom: "4px",
  display: "block",
});

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  fontFamily: "Poppins",
  fontSize: "12px",
  fontWeight: 400,
  color: theme.palette.error.main,
  paddingTop: "56px",
  margin: "0px",
  position: "absolute",
}));

const ErrorStyle = {
  borderColor: "red",
  boxShadow: "0 0 0 1px red",
};

interface Props {
  control: Control<any>;
  name: string;
  options: string[];
  selectedValue: string;
  relatedInputQuantity?: number;
  label?: string;
  rules?: RegisterOptions;
  disabled?: boolean;
  placeholder?: string;
}

export function SelectorOptions({
  control,
  name,
  options,
  selectedValue = "",
  relatedInputQuantity = 1,
  label,
  rules = {
    required: true,
  },
  disabled = false,
  placeholder,
}: Props) {
  const selectorRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  return (
    <FormControl fullWidth>
      {label && <Label>{label}</Label>}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({
          field: { onChange, ref, ...field },
          fieldState: { error },
        }) => (
          <SelectContainer>
            <StyledSelect
              ref={selectorRef}
              value={getUnitForQuantity(selectedValue, relatedInputQuantity)}
              onChange={onChange}
              style={{
                ...(error ? ErrorStyle : {}),
              }}
              sx={{
                backgroundColor: "background.white",
                "& .MuiSelect-icon": {
                  color: theme.palette.icon.primary,
                },
              }}
              disabled={disabled}
              displayEmpty
              renderValue={(value) =>
                value === "" ? placeholder : (value as React.ReactNode)
              }
              onClick={() => {
                selectorRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }}
            >
              {placeholder && (
                <MenuItem value="" disabled style={{ fontFamily: "Poppins" }}>
                  {placeholder}
                </MenuItem>
              )}
              {options.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  style={{ fontFamily: "Poppins" }}
                >
                  {getUnitForQuantity(option, relatedInputQuantity)}
                </MenuItem>
              ))}
            </StyledSelect>
            {error && (
              <StyledFormHelperText>
                {error?.message || "Obligatorio"}
              </StyledFormHelperText>
            )}
          </SelectContainer>
        )}
      />
    </FormControl>
  );
}
